import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
} from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissRegular } from "@fluentui/react-icons";
import { endOfDay, format, isValid, startOfDay, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "hooks/useAppTheme";
import { ExportPeriod } from "library/utils/exportOffers";

export type { ExportPeriod };

/** Offered period: the last two months up to today */
export const DEFAULT_EXPORT_PERIOD_MONTHS = 2;

export const getDefaultExportPeriod = (): ExportPeriod => {
  const to = endOfDay(new Date());
  return { from: startOfDay(subMonths(to, DEFAULT_EXPORT_PERIOD_MONTHS)), to };
};

/**
 * OData filter for the Azure Search query: offers requested within the period
 * (whole days, `OfferRequestedAt` is an Edm.DateTimeOffset field).
 */
export const buildExportPeriodFilter = (period: ExportPeriod): string =>
  `OfferRequestedAt ge ${startOfDay(period.from).toISOString()} and OfferRequestedAt le ${endOfDay(period.to).toISOString()}`;

type Props = {
  open: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: (period: ExportPeriod) => void;
};

/**
 * Asks for the period to export before the offers are fetched from Azure Search,
 * so an export never pulls the whole history by accident.
 */
export const ExportPeriodDialog = ({ open, title, onCancel, onConfirm }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);

  useEffect(() => {
    if (!open) return;
    const period = getDefaultExportPeriod();
    setFrom(period.from);
    setTo(period.to);
  }, [open]);

  const validationError = useMemo((): string | null => {
    if (!from || !isValid(from) || !to || !isValid(to)) {
      return t("qtg.export.period.required", "Both dates are required");
    }
    if (startOfDay(from) > endOfDay(to)) {
      return t(
        "qtg.export.period.invalid",
        "The start date must not be after the end date",
      );
    }
    return null;
  }, [from, to, t]);

  const formatDate = (date?: Date) =>
    date && isValid(date) ? format(date, "dd.MM.yyyy") : "";

  return (
    <Dialog open={open} onOpenChange={(e, data) => !data.open && onCancel()}>
      <DialogSurface style={{ maxWidth: "460px" }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={onCancel}
              />
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            <p style={{ marginTop: 0 }}>
              {t("qtg.export.period.description", {
                months: DEFAULT_EXPORT_PERIOD_MONTHS,
                defaultValue:
                  "Only offers requested in the selected period are exported. The last {{months}} months are offered.",
              })}
            </p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Field data-help="offers-export-from" label={t("qtg.export.period.from", "From")} required size="small">
                <DatePicker
                  value={from}
                  onSelectDate={(date) => setFrom(date && isValid(date) ? date : null)}
                  formatDate={formatDate}
                  placeholder="DD.MM.YYYY"
                  style={{ width: "160px" }}
                  size="small"
                />
              </Field>
              <Field data-help="offers-export-to" label={t("qtg.export.period.to", "To")} required size="small">
                <DatePicker
                  value={to}
                  onSelectDate={(date) => setTo(date && isValid(date) ? date : null)}
                  formatDate={formatDate}
                  placeholder="DD.MM.YYYY"
                  style={{ width: "160px" }}
                  size="small"
                />
              </Field>
            </div>
            {validationError && (
              <div
                style={{
                  marginTop: 12,
                  padding: 8,
                  borderRadius: 4,
                  background: theme.semanticColors.errorBackground,
                  color: theme.semanticColors.errorText,
                }}
              >
                {validationError}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>
              {t("qtg.common.cancel", "Cancel")}
            </Button>
            <Button
              data-help="offers-export-confirm"
              appearance="primary"
              disabled={!!validationError}
              onClick={() => {
                if (from && to && !validationError) {
                  onConfirm({ from, to });
                }
              }}
            >
              {t("qtg.export.period.confirm", "Export")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
